import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import { unwrap } from '../../lib/api-envelope';
import { ArrowLeft, ArrowUpDown, Truck, AlertTriangle } from 'lucide-react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { tenantTodayDate } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';

const FILTERS = ['All', 'Deliveries', 'Pickups'];

function Badge({ label, variant }: { label: string; variant: string }) {
  const colors: Record<string, string> = {
    delivery: 'bg-blue-500/20 text-blue-400',
    pickup: 'bg-purple-500/20 text-purple-400',
    good: 'bg-green-500/20 text-green-400',
    fair: 'bg-amber-500/20 text-amber-400',
    damaged: 'bg-red-500/20 text-red-400',
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${colors[variant] || 'bg-fieldiq-border text-fieldiq-muted'}`}>{label}</span>;
}

function conditionVariant(c: string): string {
  const lower = (c || '').toLowerCase();
  if (lower === 'good') return 'good';
  if (lower === 'fair') return 'fair';
  return 'damaged';
}

export default function YardMovementReport() {
  const navigate = useNavigate();
  // The TENANT's current month (companies.time_zone), not the device's. No
  // zone, no seeded range — the inputs start blank rather than guess.
  const timeZone = useTenantTimeZone();
  const now = tenantTodayDate(timeZone);
  const [startDate, setStartDate] = useState(now ? format(startOfMonth(now), 'yyyy-MM-dd') : '');
  const [endDate, setEndDate] = useState(now ? format(endOfMonth(now), 'yyyy-MM-dd') : '');
  const [filter, setFilter] = useState('All');

  const { data, isLoading } = useQuery({
    queryKey: ['yard-movement', startDate, endDate],
    queryFn: () => apiClient.get('/surface-rentals/reports/yard-movement', { params: { start_date: startDate, end_date: endDate } }).then(r => unwrap<any>(r.data)),
  });

  const movements: any[] = useMemo(() => {
    let list = data?.movements || [];
    if (filter === 'Deliveries') list = list.filter((m: any) => (m.dispatch_type || '').toLowerCase() === 'delivery');
    if (filter === 'Pickups') list = list.filter((m: any) => (m.dispatch_type || '').toLowerCase() === 'pickup');
    return list;
  }, [data, filter]);

  // API: { period, movements, summary: { total_movements, deliveries, pickups,
  // damage_count } }. All three tiles read the top level, and the row filters
  // read m.type where the API sends m.dispatch_type — so the Deliveries and
  // Pickups filter pills matched nothing and every KPI fell back to a count of
  // rows that had already been filtered to zero.
  const summary = data?.summary;
  const totalMovements = summary?.total_movements ?? movements.length;
  const deliveries = summary?.deliveries ?? movements.filter((m: any) => (m.dispatch_type || '').toLowerCase() === 'delivery').length;
  // The server also counts 'poor' as damage; matching only 'damaged' under-reports.
  const damageIncidents = summary?.damage_count ?? movements.filter((m: any) => ['damaged', 'poor'].includes((m.condition_in || '').toLowerCase()) || ['damaged', 'poor'].includes((m.condition_out || '').toLowerCase())).length;

  const isEmpty = !isLoading && movements.length === 0;

  return (
    <div className="min-h-full pb-[var(--bottom-chrome)] bg-background">
      
      <div className="border-b border-border px-6 py-4">
        <button onClick={() => navigate('/reports')} className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors">
          <ArrowLeft size={24} /> Back
        </button>
        <div className="flex items-center justify-between mt-2">
          <h1 className="text-base font-bold text-foreground">Yard Movement Log</h1>
        </div>
      </div>

      <div className="px-6 py-4 space-y-4">
        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3">
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-fieldiq-card border border-fieldiq-border rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-fieldiq-accent" />
          <span className="text-fieldiq-muted text-xs">to</span>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-fieldiq-card border border-fieldiq-border rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-fieldiq-accent" />
        </div>

        <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
          {FILTERS.map(f => (
            <button key={f} onClick={() => setFilter(f)} className={`whitespace-nowrap px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${filter === f ? 'bg-fieldiq-accent text-fieldiq-dark' : 'bg-fieldiq-card text-fieldiq-muted hover:text-white'}`}>{f}</button>
          ))}
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg p-3 text-center">
            <ArrowUpDown className="w-4 h-4 text-fieldiq-accent mx-auto mb-1" />
            <p className="text-lg font-bold text-white">{totalMovements}</p>
            <p className="text-[10px] text-fieldiq-muted">Total Movements</p>
          </div>
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg p-3 text-center">
            <Truck className="w-4 h-4 text-blue-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-white">{deliveries}</p>
            <p className="text-[10px] text-fieldiq-muted">Deliveries</p>
          </div>
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg p-3 text-center">
            <AlertTriangle className="w-4 h-4 text-red-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-white">{damageIncidents}</p>
            <p className="text-[10px] text-fieldiq-muted">Damage Incidents</p>
          </div>
        </div>

        {isEmpty ? (
          <div className="text-center py-16">
            <ArrowUpDown size={40} className="mx-auto text-fieldiq-muted mb-3" />
            <p className="text-fieldiq-muted text-sm">No yard movements for this period.</p>
          </div>
        ) : (
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-fieldiq-border text-fieldiq-muted text-xs">
                    <th className="text-left px-3 py-2.5 font-medium">Date</th>
                    <th className="text-left px-3 py-2.5 font-medium">Type</th>
                    <th className="text-left px-3 py-2.5 font-medium">Asset</th>
                    <th className="text-left px-3 py-2.5 font-medium">Customer</th>
                    <th className="text-left px-3 py-2.5 font-medium">Site</th>
                    <th className="text-left px-3 py-2.5 font-medium">Driver/Tech</th>
                    <th className="text-left px-3 py-2.5 font-medium">Cond. Out</th>
                    <th className="text-left px-3 py-2.5 font-medium">Cond. In</th>
                    <th className="text-left px-3 py-2.5 font-medium">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.map((m: any, i: number) => {
                    const hasDamage = ['damaged', 'poor'].includes((m.condition_in || '').toLowerCase()) || ['damaged', 'poor'].includes((m.condition_out || '').toLowerCase());
                    return (
                      <tr key={i} className={`border-b border-fieldiq-border/50 ${hasDamage ? 'border-l-2 border-l-red-500' : ''}`}>
                        <td className="px-3 py-2.5 text-fieldiq-muted">{m.scheduled_date}</td>
                        <td className="px-3 py-2.5"><Badge label={m.dispatch_type || 'Delivery'} variant={(m.dispatch_type || 'delivery').toLowerCase()} /></td>
                        <td className="px-3 py-2.5 text-white font-medium">{m.asset_name}</td>
                        <td className="px-3 py-2.5 text-fieldiq-muted">{m.customer_name}</td>
                        <td className="px-3 py-2.5 text-fieldiq-muted">{m.site_location}</td>
                        <td className="px-3 py-2.5 text-fieldiq-muted">{m.driver_name}</td>
                        <td className="px-3 py-2.5">{m.condition_out ? <Badge label={m.condition_out} variant={conditionVariant(m.condition_out)} /> : '—'}</td>
                        <td className="px-3 py-2.5">{m.condition_in ? <Badge label={m.condition_in} variant={conditionVariant(m.condition_in)} /> : '—'}</td>
                        <td className="px-3 py-2.5 text-fieldiq-muted text-xs max-w-[200px] truncate">{m.notes || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {isLoading && (
          <div className="text-center py-16">
            <div className="w-8 h-8 border-2 border-fieldiq-accent border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        )}
      </div>
    </div>
  );
}
